import { createSvgEl } from '@learnforge/demo-render';
import type { Point } from '../finance/series';
import type { ChartHandle } from './chart';

export interface Band {
  id: string;
  points: readonly Point[];
  className: string;
}

export interface AreaStackHandle {
  /** Bands stacked bottom→top (cumulative); each becomes a filled polygon between its lower and upper cumulative edges. */
  setBands(bands: readonly Band[]): void;
  clear(): void;
  destroy(): void;
}

export function createAreaStack(chart: ChartHandle): AreaStackHandle {
  // Map from band id → path element
  const paths = new Map<string, SVGPathElement>();

  /**
   * Build the closed path 'd' string for one stacked band.
   * upperEdge: screen points for the top of this band (left→right)
   * lowerEdge: screen points for the bottom of this band (right→left)
   */
  function buildD(upperEdge: readonly Point[], lowerEdge: readonly Point[]): string {
    if (upperEdge.length === 0) return '';

    const parts: string[] = [];

    // Top edge: left → right
    const u0 = chart.toScreen(upperEdge[0]!);
    parts.push(`M ${u0.x} ${u0.y}`);
    for (let i = 1; i < upperEdge.length; i++) {
      const { x, y } = chart.toScreen(upperEdge[i]!);
      parts.push(`L ${x} ${y}`);
    }

    // Bottom edge: right → left (close the ribbon)
    for (let i = lowerEdge.length - 1; i >= 0; i--) {
      const { x, y } = chart.toScreen(lowerEdge[i]!);
      parts.push(`L ${x} ${y}`);
    }

    parts.push('Z');
    return parts.join(' ');
  }

  function setBands(bands: readonly Band[]): void {
    // Collect cumulative lower values per index across bands.
    // Assumption (per plan): all bands share matching t sequences (aligned by index).
    const pointCount = bands.length > 0 ? bands[0]!.points.length : 0;

    // cumulativeLower[i] = running sum of band values at index i, before this band
    const cumulativeLower: number[] = new Array<number>(pointCount).fill(0);

    // Track which ids appear in this call (for stale-removal below)
    const seenIds = new Set<string>();

    for (const band of bands) {
      seenIds.add(band.id);

      // Build upper edge data points by adding this band's value to cumulative lower
      const lowerPoints: Point[] = band.points.map((p, i) => ({
        t: p.t,
        value: cumulativeLower[i]!,
      }));

      const upperPoints: Point[] = band.points.map((p, i) => ({
        t: p.t,
        value: cumulativeLower[i]! + p.value,
      }));

      // Reuse or create the path element for this id
      let path = paths.get(band.id);
      if (!path) {
        path = createSvgEl('path', {});
        chart.plotGroup.appendChild(path);
        paths.set(band.id, path);
      }

      path.setAttribute('d', buildD(upperPoints, lowerPoints));
      path.setAttribute('class', band.className);

      // Advance cumulative lower for the next band
      for (let i = 0; i < band.points.length; i++) {
        cumulativeLower[i]! += band.points[i]!.value;
      }
    }

    // Remove paths for ids no longer in the band list
    for (const [id, path] of paths) {
      if (!seenIds.has(id)) {
        path.parentNode?.removeChild(path);
        paths.delete(id);
      }
    }
  }

  function clear(): void {
    for (const path of paths.values()) {
      path.parentNode?.removeChild(path);
    }
    paths.clear();
  }

  function destroy(): void {
    clear();
  }

  return { setBands, clear, destroy };
}
